import { Routes, Route } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { AppStateProvider } from "@/context/AppStateProvider";
import { FullscreenProvider } from '@/context/FullscreenContext';
import { ProtectedRoute } from "@/components/common/ProtectedRoute";
import * as Sentry from "@sentry/react";

// Lazy load pages
const HomePage = lazy(() => import("@/pages/HomePage"));
const ManageQuizPage = lazy(() => import("@/pages/ManageQuizPage"));
const QuizEditorPage = lazy(() => import("@/pages/QuizEditorPage"));
const TakeQuizPage = lazy(() => import('@/pages/TakeQuizPage'));

// Lazy load Settings Components
const BrowserSettings = lazy(() => import("@/components/quiz/settings/components/BrowserSettings"));
const GeneralSettings = lazy(() => import("@/components/quiz/settings/components/GeneralSettings"));
const QuizTracking = lazy(() => import("@/components/quiz/settings/components/QuizTracking"));
const ScoringOptions = lazy(() => import("@/components/quiz/settings/components/ScoringOptions"));
const SmartProctoring = lazy(() => import("@/components/quiz/settings/components/SmartProctoring"));
const LeadIntegration = lazy(() => import("@/components/quiz/settings/components/LeadIntegration"));

// Lazy load Results Components
const QuizResults = lazy(() => import("@/components/take-quiz/ui/QuizResults"));
const QuizResult = lazy(() => import('@/components/results/QuizResult'));
const QuizReport = lazy(() => import('@/components/results/QuizReport'));
const ResultPageSettings = lazy(() => import('@/components/results/ResultPageSettings'));

const Loading: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
)

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

function App() {
  return (
    <AppStateProvider>
      <FullscreenProvider>
        <Suspense fallback={<Loading />}>
          <SentryRoutes>
            <Route path="/" element={<HomePage />} />
            <Route
              path="/quiz-title/:quizId"
              element={<TakeQuizPage />}
            />
            <Route
              path="/quiz-result/:quizId"
              element={
                <ProtectedRoute>
                  <QuizResult />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quiz-report/:quizId/:id"
              element={
                <ProtectedRoute>
                  <QuizReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/view-result-settings/:quizId"
              element={
                <ProtectedRoute>
                  <ResultPageSettings />
                </ProtectedRoute>
              }
            />

            {/* Add route for quiz preview */}
            <Route
              path="/preview/:quizTitle"
              element={<TakeQuizPage />}
            />
            <Route
              path="/quiz-results/:quizSlug"
              element={<QuizResults />}
            />
            <Route
              path="/manage/:page"
              element={
                <ProtectedRoute>
                  <ManageQuizPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage"
              element={
                <ProtectedRoute>
                  <ManageQuizPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quiz/:id"
              element={
                <ProtectedRoute>
                  <QuizEditorPage />
                </ProtectedRoute>
              }
            />

            {/* Settings Routes */}
            <Route
              path="/general-settings/:quizId"
              element={
                <ProtectedRoute>
                  <GeneralSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/security-settings/:quizId"
              element={
                <ProtectedRoute>
                  <BrowserSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quiz-tracking/:quizId"
              element={
                <ProtectedRoute>
                  <QuizTracking />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scoring-option/:quizId"
              element={
                <ProtectedRoute>
                  <ScoringOptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/smart-proctoring-system/:quizId"
              element={
                <ProtectedRoute>
                  <SmartProctoring />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lead-integration/:quizId"
              element={
                <ProtectedRoute>
                  <LeadIntegration />
                </ProtectedRoute>
              }
            />
          </SentryRoutes>
        </Suspense>
      </FullscreenProvider>
    </AppStateProvider>
  );
} export default App;